import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, patch, del, toastError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { label } from "@/lib/labels";
import { StatusBadge } from "@/components/common/StatusBadge";
import { LoadingState, ErrorState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertTriangle, ArrowLeftRight, Boxes, CalendarRange, CheckCircle2, ExternalLink, FolderKanban, Loader2, MapPin, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { differenceInCalendarDays, parseISO } from "date-fns";

const ALLOC_STATUSES = ["planlagt", "bekreftet", "aktiv", "fullfort", "kansellert"];
const days = (a, b) => (a && b ? Math.max(0, differenceInCalendarDays(parseISO(b), parseISO(a))) : 0);

/**
 * Allocation detail + resolve panel.
 * Reads GET /planning/allocations/{id}; writes via PATCH /allocations/{id} (dates, status, module swap, force) and DELETE.
 */
export const AllocationSheet = ({ allocationId, open, onOpenChange, canWrite, onChanged }) => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ["allocation", allocationId], queryFn: () => get(`/planning/allocations/${allocationId}`), enabled: !!(open && allocationId) });
  const [dates, setDates] = useState({ start: "", end: "" });
  const [notes, setNotes] = useState("");
  const [swapTo, setSwapTo] = useState(null);
  const [forceCtx, setForceCtx] = useState(null); // {body, conflicts}
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  useEffect(() => {
    if (data) {
      setDates({ start: data.start_date, end: data.end_date });
      setNotes(data.notes || "");
      setSwapTo(null);
      setForceCtx(null);
    }
  }, [data]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["planning"] });
    qc.invalidateQueries({ queryKey: ["capacity"] });
    qc.invalidateQueries({ queryKey: ["allocation", allocationId] });
    qc.invalidateQueries({ queryKey: ["project", data?.project_id] });
    qc.invalidateQueries({ queryKey: ["modules"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    onChanged?.();
  };

  const update = useMutation({
    mutationFn: (body) => patch(`/allocations/${allocationId}`, body),
    onSuccess: (_r, body) => {
      const msg = body.module_id ? "Modul byttet" : body.status ? `Status satt til ${label(body.status)}` : body.start_date || body.end_date ? "Periode oppdatert" : "Allokering oppdatert";
      toast.success(msg);
      setForceCtx(null);
      invalidate();
    },
    onError: (e, body) => {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.conflicts) setForceCtx({ body, conflicts: d.conflicts, message: d.message });
      else toastError(e);
    },
  });
  const remove = useMutation({
    mutationFn: () => del(`/allocations/${allocationId}`),
    onSuccess: () => { toast.success("Allokering fjernet"); invalidate(); onOpenChange(false); },
    onError: (e) => toastError(e),
  });

  const datesChanged = data && (dates.start !== data.start_date || dates.end !== data.end_date);
  const datesValid = dates.start && dates.end && dates.end > dates.start;
  const conflictCount = data?.conflicts?.length || 0;
  const alternatives = useMemo(() => data?.alternatives || [], [data]);
  const locked = data && ["fullfort", "kansellert"].includes(data.status);

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-[540px] max-w-[96vw] overflow-y-auto p-0 flex flex-col" data-testid="allocation-sheet">
          {isLoading || !data ? (
            error ? <div className="p-6"><ErrorState error={error} onRetry={refetch} /></div> : <LoadingState className="py-24" />
          ) : (
            <>
              <div className="px-6 pt-6 pb-4 border-b">
                <SheetHeader className="space-y-1.5 text-left">
                  <div className="text-[12px] text-muted-foreground">Allokering</div>
                  <SheetTitle className="font-display text-xl tracking-tight flex flex-wrap items-center gap-2">
                    <span className="font-mono" data-testid="allocation-sheet-module-code">{data.module?.module_id}</span>
                    <StatusBadge status={data.status} entity="alloc" />
                    <StatusBadge status={data.type} entity="alloc-type" />
                  </SheetTitle>
                  <SheetDescription className="text-sm">{data.module_type?.name}{data.module_type?.capacity_persons ? ` · ${data.module_type.capacity_persons} pers.` : ""}</SheetDescription>
                </SheetHeader>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-md border p-3">
                    <div className="text-[11px] text-muted-foreground mb-1">Periode</div>
                    <div className="font-medium tabular" data-testid="allocation-sheet-period">{fmtDate(data.start_date, "d. MMM yyyy")} – {fmtDate(data.end_date, "d. MMM yyyy")}</div>
                    <div className="text-xs text-muted-foreground tabular">{days(data.start_date, data.end_date)} dager</div>
                  </div>
                  <div className="rounded-md border p-3 min-w-0">
                    <div className="text-[11px] text-muted-foreground mb-1">Prosjekt</div>
                    {data.project?.id ? (
                      <button type="button" className="font-medium text-left truncate w-full hover:underline" onClick={() => navigate(`/prosjekter/${data.project.id}?tab=moduler`)} data-testid="allocation-sheet-project-link">{data.project.name}</button>
                    ) : <div className="text-muted-foreground">Uten prosjekt</div>}
                    <div className="text-xs text-muted-foreground truncate font-mono">{data.project?.project_number}{data.customer?.name ? ` · ${data.customer.name}` : ""}</div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {data.module?.current_location && <span>Modul: {data.module.current_location}</span>}
                  {data.project?.address && <span>Prosjekt: {data.project.address}</span>}
                </div>
              </div>

              <div className="px-6 py-5 space-y-5 flex-1">
                {conflictCount > 0 && (
                  <Alert className="border-rose-300 bg-rose-50 dark:bg-rose-950/30 dark:border-rose-900" data-testid="allocation-sheet-conflict-alert">
                    <AlertTriangle className="h-4 w-4 text-rose-700" />
                    <AlertTitle className="text-rose-900 dark:text-rose-100 text-sm">Dobbelbooking – {conflictCount} overlappende allokering{conflictCount === 1 ? "" : "er"}</AlertTitle>
                    <AlertDescription className="text-xs text-rose-900 dark:text-rose-100">
                      <ul className="mt-1 space-y-1">
                        {data.conflicts.map((c) => (
                          <li key={c.id} className="flex items-center gap-2">
                            <span className="font-mono">{c.project_number || label(c.type)}</span>
                            <span className="truncate flex-1">{c.project_name}</span>
                            <span className="tabular shrink-0">{fmtDate(c.start_date, "d. MMM")} – {fmtDate(c.end_date, "d. MMM")}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-2 text-[11px] opacity-80">Løs ved å bytte modul, justere periode eller kansellere.</div>
                    </AlertDescription>
                  </Alert>
                )}
                {conflictCount === 0 && !locked && (
                  <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:bg-emerald-950/30 dark:border-emerald-900 dark:text-emerald-200" data-testid="allocation-sheet-ok">
                    <CheckCircle2 className="h-4 w-4" /> Ingen konflikt for denne allokeringen
                  </div>
                )}

                {canWrite && !locked ? (
                  <Tabs defaultValue={conflictCount ? "swap" : "dates"} className="w-full">
                    <TabsList className="grid w-full grid-cols-3 h-9" data-testid="allocation-sheet-tabs">
                      <TabsTrigger value="dates" data-testid="allocation-tab-dates">Periode</TabsTrigger>
                      <TabsTrigger value="swap" data-testid="allocation-tab-swap">Bytt modul {alternatives.length ? <span className="ml-1 font-mono text-[10.5px] text-primary tabular">{alternatives.length}</span> : null}</TabsTrigger>
                      <TabsTrigger value="status" data-testid="allocation-tab-status">Status</TabsTrigger>
                    </TabsList>

                    <TabsContent value="dates" className="mt-3 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs">Fra</Label><Input type="date" value={dates.start} onChange={(e) => setDates({ ...dates, start: e.target.value })} data-testid="allocation-start-input" /></div>
                        <div><Label className="text-xs">Til</Label><Input type="date" value={dates.end} onChange={(e) => setDates({ ...dates, end: e.target.value })} data-testid="allocation-end-input" /></div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 text-xs">
                        {[["-7", "−1 uke"], ["+7", "+1 uke"], ["+14", "+2 uker"], ["+30", "+1 mnd"]].map(([d, l]) => (
                          <Button key={d} type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => { const n = Number(d); const e = parseISO(dates.end); e.setDate(e.getDate() + n); setDates({ ...dates, end: e.toISOString().slice(0, 10) }); }} data-testid={`allocation-extend-${d}`}>{l} slutt</Button>
                        ))}
                      </div>
                      <div className="text-xs text-muted-foreground tabular">{datesValid ? `${days(dates.start, dates.end)} dager` : "Sluttdato må være etter startdato"}</div>
                      <Button className="w-full" disabled={!datesChanged || !datesValid || update.isPending} onClick={() => update.mutate({ start_date: dates.start, end_date: dates.end })} data-testid="allocation-save-dates-button">
                        {update.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarRange className="h-4 w-4 mr-2" />} Lagre ny periode
                      </Button>
                    </TabsContent>

                    <TabsContent value="swap" className="mt-3 space-y-3">
                      <div className="text-xs text-muted-foreground">Ledige moduler av samme type ({data.module_type?.code}) i hele perioden. Bytte frigjør {data.module?.module_id}.</div>
                      {alternatives.length === 0 ? (
                        <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground" data-testid="allocation-no-alternatives">Ingen ledige moduler av samme type i perioden. Juster periode eller kanseller.</div>
                      ) : (
                        <div className="max-h-56 overflow-y-auto rounded-md border divide-y" data-testid="allocation-alternatives-list">
                          {alternatives.map((m) => (
                            <button key={m.id} type="button" onClick={() => setSwapTo(m)} className={cn("flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring", swapTo?.id === m.id && "bg-primary/[0.06]")} data-testid={`allocation-alternative-${m.module_id}`}>
                              <span className={cn("h-4 w-4 rounded-full border-2 shrink-0", swapTo?.id === m.id ? "border-primary bg-primary" : "border-muted-foreground/40")} />
                              <span className="font-mono text-xs">{m.module_id}</span>
                              <StatusBadge status={m.status} entity="module" className="text-[10px] px-1.5" />
                              <span className="ml-auto truncate text-xs text-muted-foreground">{m.current_location}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      <Button className="w-full" disabled={!swapTo || update.isPending} onClick={() => update.mutate({ module_id: swapTo.id })} data-testid="allocation-swap-button">
                        {update.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowLeftRight className="h-4 w-4 mr-2" />} Bytt til {swapTo?.module_id || "valgt modul"}
                      </Button>
                    </TabsContent>

                    <TabsContent value="status" className="mt-3 space-y-3">
                      <div>
                        <Label className="text-xs">Status</Label>
                        <Select value={data.status} onValueChange={(v) => (v === "kansellert" ? setConfirmCancel(true) : update.mutate({ status: v }))}>
                          <SelectTrigger data-testid="allocation-status-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{ALLOC_STATUSES.map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Type</Label>
                        <Select value={data.type} onValueChange={(v) => update.mutate({ type: v })}>
                          <SelectTrigger data-testid="allocation-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{["reservert", "klargjoring", "prosjekt", "retur"].map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Notat</Label>
                        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="allocation-notes-input" />
                        {notes !== (data.notes || "") && <Button size="sm" variant="outline" className="mt-2" onClick={() => update.mutate({ notes })} data-testid="allocation-save-notes-button">Lagre notat</Button>}
                      </div>
                      <Separator />
                      <div className="flex gap-2">
                        <Button variant="outline" className="flex-1 border-rose-300 text-rose-800 hover:bg-rose-50 dark:text-rose-200" onClick={() => setConfirmCancel(true)} data-testid="allocation-cancel-button">Kanseller allokering</Button>
                        <Button variant="ghost" className="text-muted-foreground" onClick={() => setConfirmDelete(true)} data-testid="allocation-delete-button"><Trash2 className="h-4 w-4 mr-1" /> Slett</Button>
                      </div>
                    </TabsContent>
                  </Tabs>
                ) : locked ? (
                  <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground" data-testid="allocation-locked">Allokeringen er {label(data.status)} og kan ikke endres.</div>
                ) : null}

                {forceCtx && (
                  <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900" data-testid="allocation-force-alert">
                    <AlertTriangle className="h-4 w-4 text-amber-700" />
                    <AlertTitle className="text-amber-900 dark:text-amber-100 text-sm">{forceCtx.message || "Endringen gir dobbelbooking"}</AlertTitle>
                    <AlertDescription className="text-xs text-amber-900 dark:text-amber-100">
                      <ul className="mt-1 space-y-0.5">{forceCtx.conflicts.map((c) => <li key={c.id}>{c.project_number || ""} {c.project_name || label(c.type)} · <span className="tabular">{fmtDate(c.start_date)} – {fmtDate(c.end_date)}</span></li>)}</ul>
                      <div className="mt-2 flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setForceCtx(null)} data-testid="allocation-force-cancel-button">Avbryt</Button>
                        <Button size="sm" variant="destructive" disabled={update.isPending} onClick={() => update.mutate({ ...forceCtx.body, force: true })} data-testid="allocation-force-button">Lagre likevel (registrer konflikt)</Button>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                <Separator />
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-[13px] font-semibold">Andre allokeringer på {data.module?.module_id}</h3>
                    <span className="text-xs text-muted-foreground tabular">{data.others?.length || 0}</span>
                  </div>
                  {(data.others || []).length === 0 ? (
                    <div className="text-xs text-muted-foreground">Ingen andre allokeringer registrert.</div>
                  ) : (
                    <ul className="divide-y rounded-md border text-sm" data-testid="allocation-others-list">
                      {data.others.map((o) => {
                        const overlaps = o.start_date < data.end_date && o.end_date > data.start_date && !["kansellert", "fullfort"].includes(o.status);
                        return (
                          <li key={o.id} className={cn("flex items-center gap-2 px-3 py-2", overlaps && "bg-rose-50/60 dark:bg-rose-950/20")}>
                            <StatusBadge status={o.type} entity="alloc-type" className="text-[10px] px-1.5" />
                            <button type="button" className="min-w-0 flex-1 truncate text-left hover:underline" onClick={() => o.project_id && navigate(`/prosjekter/${o.project_id}?tab=moduler`)}>{o.project_number} {o.project_name || "(uten prosjekt)"}</button>
                            <span className="text-xs text-muted-foreground tabular shrink-0">{fmtDate(o.start_date, "d. MMM yy")} – {fmtDate(o.end_date, "d. MMM yy")}</span>
                            {overlaps && <AlertTriangle className="h-3.5 w-3.5 text-rose-600 shrink-0" />}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>

              <div className="border-t px-6 py-3 flex flex-wrap gap-2">
                {data.project?.id && <Button variant="outline" size="sm" onClick={() => navigate(`/prosjekter/${data.project.id}?tab=moduler`)} data-testid="allocation-open-project-button"><FolderKanban className="h-4 w-4 mr-1.5" /> Åpne prosjekt</Button>}
                <Button variant="outline" size="sm" onClick={() => navigate(`/moduler/${data.module?.id}`)} data-testid="allocation-open-module-button"><ExternalLink className="h-4 w-4 mr-1.5" /> Åpne modul</Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent data-testid="allocation-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Kansellere allokeringen?</AlertDialogTitle>
            <AlertDialogDescription>Modulen {data?.module?.module_id} frigjøres for perioden {fmtDate(data?.start_date)} – {fmtDate(data?.end_date)}. Handlingen logges i historikken.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="allocation-cancel-dialog-abort">Avbryt</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => update.mutate({ status: "kansellert" })} data-testid="allocation-cancel-dialog-confirm">Kanseller</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="allocation-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette allokeringen?</AlertDialogTitle>
            <AlertDialogDescription>Allokeringen fjernes fra planen. Bruk «Kanseller» hvis du vil beholde sporbarhet i planen.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="allocation-delete-dialog-abort">Avbryt</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => remove.mutate()} data-testid="allocation-delete-dialog-confirm">Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

